import type { ReplyDraft } from './types';

/** The wizard's steps: choose a position, then add and remove the reply's points. */
export type WizardStepKey = 'card' | 'review';

export type DraftStep = 'loading' | WizardStepKey;

/**
 * The steps in the order the expert walks them. A new step, such as picking
 * references, is added here, and moving forward and back follow from it.
 */
export const WIZARD_STEP_ORDER: readonly WizardStepKey[] = ['card', 'review'];

/**
 * Which step the draft shows, from the server's view and the step the expert
 * last moved to.
 *
 * Without a draft only the card can show, whatever the expert asked for, so
 * reopening a draft lands where the server holds it and no draft text is read
 * before a position is chosen. With one, the expert's own step wins: going
 * back to the card keeps the earlier option's draft in the cache, hidden.
 */
export function deriveStep(draft: ReplyDraft | undefined, requested: WizardStepKey): DraftStep {
  if (!draft) {
    return 'loading';
  }

  return draft.draft ? requested : 'card';
}

/** The step after this one, or null on the last, where the draft is used. */
export function nextStep(step: WizardStepKey): WizardStepKey | null {
  return WIZARD_STEP_ORDER[WIZARD_STEP_ORDER.indexOf(step) + 1] ?? null;
}

/** The step before this one, or null on the first. */
export function previousStep(step: WizardStepKey): WizardStepKey | null {
  const index = WIZARD_STEP_ORDER.indexOf(step);

  return index > 0 ? WIZARD_STEP_ORDER[index - 1] : null;
}
